/**
 * Lightweight, deterministic grammar guard applied to AI-generated and
 * auto-populated text across the app — NOT a full grammar checker, just a
 * safety net for the most common and most visible slip: a sentence (or the
 * very first word of a block/heading/list item/cell) coming back from the
 * model starting with a lowercase letter, and the standalone pronoun "I"
 * losing its capital.
 *
 * Deliberately conservative: it only ever flips an existing lowercase letter
 * to uppercase at the same character position (never inserts/deletes/re-
 * wraps text), so it's safe to re-run on already-guarded content (idempotent)
 * and safe to run on HTML — capitalizing a letter never changes markup.
 */

// Abbreviations whose internal/trailing "." must NOT be treated as a sentence
// boundary (clinical protocols use these constantly) — e.g. "administered
// i.v. twice daily" or "the dose (e.g. 5 mg)" must not capitalize the word
// that follows.
const NON_SENTENCE_ENDING_ABBREVIATIONS = new Set([
  'e.g.', 'i.e.', 'etc.', 'vs.', 'cf.', 'approx.', 'no.', 'fig.', 'figs.',
  'mg.', 'ml.', 'kg.', 'hr.', 'hrs.', 'min.', 'mins.', 'sec.', 'secs.',
  'dr.', 'mr.', 'mrs.', 'ms.', 'prof.', 'al.',
  'i.v.', 'i.m.', 'i.p.', 's.c.', 'p.o.', 'q.d.', 'b.i.d.', 't.i.d.', 'q.i.d.',
  // Company suffixes: "BeOne Medicines Ltd. will ensure" is mid-sentence.
  'ltd.', 'inc.', 'corp.', 'co.', 'llc.',
]);

// A lowercase start that must stay lowercase even at a sentence boundary: URLs
// ("PMID: 123. https://…" was becoming "Https://…"), mixed-case terms whose case
// is meaningful (mRNA, eGFR, pH, iPSC), and unit symbols, which often open a
// table cell ("mg/dL" was becoming "Mg/dL").
// Unit followed by "/" (mg/dL) or standing alone, as in a glossary or lab table cell.
const UNIT_TOKEN_RE = /^(?:mg|mcg|µg|kg|g|ng|pg|mmol|µmol|nmol|pmol|mol|ml|mL|dl|dL|l|iu|mm|cm)(?:\/|\s*$)/;
function isCaseSensitiveToken(followingText) {
  return (
    /^(?:https?:\/\/|ftp:\/\/|www\.|doi:)/i.test(followingText) ||
    /^[a-z]+[A-Z]/.test(followingText) ||
    UNIT_TOKEN_RE.test(followingText)
  );
}

function endsWithNonSentenceAbbreviation(precedingText) {
  const trimmed = precedingText.replace(/\s+$/, '');
  const match = trimmed.match(/([A-Za-z]+(?:\.[A-Za-z]+)*\.)$/);
  if (!match) return false;
  return NON_SENTENCE_ENDING_ABBREVIATIONS.has(match[1].toLowerCase());
}

/**
 * Capitalizes the first letter of the string and the first letter following
 * a real sentence boundary (". "/"! "/"? " or a line break) — skips known
 * abbreviations (see above) so "e.g. paracetamol" isn't mangled into
 * "E.g. Paracetamol".
 */
export function capitalizeSentencesText(input) {
  if (!input) return input;
  const text = String(input);
  return text.replace(
    /(^\s*|[.!?]+\s+|\n+\s*)([a-z])/g,
    (match, boundary, letter, offset, fullString) => {
      if (isCaseSensitiveToken(fullString.slice(offset + boundary.length))) return match;
      if (boundary && /[.!?]/.test(boundary)) {
        const precedingText = fullString.slice(0, offset + boundary.length);
        if (endsWithNonSentenceAbbreviation(precedingText)) return match;
      }
      return boundary + letter.toUpperCase();
    },
  );
}

/** Standalone lowercase "i" pronoun -> "I" — skips "i.v."/"i.m."/"i.p."/etc.
 * (an "i" immediately followed by "." with no space is always one of those
 * clinical abbreviations here, never the pronoun). */
export function capitalizePronounI(input) {
  if (!input) return input;
  return String(input).replace(/\bi\b(?!\.)/g, 'I');
}

/** Plain-text guard — sentence capitalization + the "I" pronoun fix. Safe to
 * call on markdown or lightly-tagged strings too (see capitalizeSentencesText's
 * doc comment: it only ever flips case, never restructures text). */
export function applyGrammarGuards(input) {
  if (!input) return input;
  let out = capitalizeSentencesText(input);
  out = capitalizePronounI(out);
  return out;
}

const BLOCK_SELECTOR =
  'p, li, h1, h2, h3, h4, h5, h6, td, th, dd, dt, blockquote, caption, figcaption, div';

/**
 * DOM-independent core of the HTML-aware guard, factored out so it's unit-
 * testable without a real DOM: given one block's text-node contents IN
 * ORDER, returns them transformed with the SAME lengths (so each result[i]
 * is always safe to write back into the node that produced segments[i]).
 * Concatenating first (rather than guarding each segment in isolation) lets
 * capitalization decisions see the whole sentence even when it's split
 * across inline tags, e.g. "the <strong>study</strong> drug" — three text
 * segments, one logical sentence.
 */
export function applyGrammarGuardsToSegments(segments) {
  if (!Array.isArray(segments) || segments.length === 0) return segments;
  const combined = segments.join('');
  const transformed = applyGrammarGuards(combined);
  if (transformed === combined) return segments;

  const result = [];
  let cursor = 0;
  segments.forEach((seg) => {
    const len = seg.length;
    result.push(transformed.slice(cursor, cursor + len));
    cursor += len;
  });
  return result;
}

/**
 * HTML-aware version — walks each block-level element's text nodes, guards
 * them together via applyGrammarGuardsToSegments (so inline markup like
 * <strong> never breaks a sentence's capitalization), then writes each
 * transformed segment back into the text node it came from. Because the
 * guard never changes string length, this never touches markup — only text.
 *
 * Falls back to the plain-text guard when there's no markup to parse (or no
 * DOM available, e.g. if ever imported into a non-browser bundle).
 */
export function applyGrammarGuardsToHtml(html) {
  if (!html) return html;
  if (typeof document === 'undefined' || !/<[a-z][\s\S]*>/i.test(html)) {
    return applyGrammarGuards(html);
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  const blocks = Array.from(container.querySelectorAll(BLOCK_SELECTOR));
  const targets = blocks.length > 0 ? blocks : [container];

  targets.forEach((block) => {
    // Only process the innermost block in any ancestor/descendant pair found
    // in `targets` (e.g. a <li> inside a <div>) — otherwise the outer one's
    // pass would re-process (harmlessly, but redundantly) the inner one's
    // already-transformed text.
    if (targets.some((other) => other !== block && other.contains(block))) return;

    const textNodes = [];
    const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      textNodes.push(node);
      node = walker.nextNode();
    }
    if (textNodes.length === 0) return;

    const transformedSegments = applyGrammarGuardsToSegments(textNodes.map((n) => n.nodeValue));
    textNodes.forEach((n, i) => {
      n.nodeValue = transformedSegments[i];
    });
  });

  return container.innerHTML;
}
